import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { Subscription, Subject } from 'rxjs';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { Injectable } from '@angular/core';

@Injectable()
export class ModalRouteService {
	modalOpen: boolean;
	subscriptions = new Subscription();
	entityPathMap = {
		mediaclip: 'library/mediaclip',
		globalprebidsettings: 'publication/global-prebid-settings',
		playlist: 'library/playlist',
		subtitle: 'mediaclip/:parentid/subtitle'
	};
	defaultEntityTab = {
		mediaclip: 'metadata',
		playlist: 'general',
		globalprebidsettings: 'auction'
	};
	onCloseSubject = new Subject<void>();
	previousUrl = '';
	navigationStartTrigger = '';
	modalRegex = new RegExp(/\(modal:(.*?)\)/);

	constructor(
		private router: Router,
		private hotkeysService: HotkeysService
	) {
		this.subscriptions.add(this.router.events.subscribe((event) => {
			if (event instanceof NavigationStart) {
				this.navigationStartTrigger = event.navigationTrigger;
			}
			if (event instanceof NavigationEnd) {
				if (this.previousUrl && event.urlAfterRedirects) {
					const prevMatch = this.modalRegex.exec(this.previousUrl);
					const urlMatch = this.modalRegex.exec(event.urlAfterRedirects);
					if (prevMatch && urlMatch && prevMatch[0] && prevMatch[0] === urlMatch[0] && this.navigationStartTrigger !== 'popstate') {
						// same modal URL so close it to show the primary URL change
						this.close();
					}
					if (prevMatch && !urlMatch) {
						// was closed
						this.onCloseSubject.next();
					}
				}
				this.previousUrl = event.urlAfterRedirects;
				this.modalOpen = event.urlAfterRedirects && event.urlAfterRedirects.includes('modal:');
			}
		}));

		this.hotkeysService.add(new Hotkey('escape', (event: KeyboardEvent): boolean => {
			if (this.modalOpen) {
				event.preventDefault();
				event.stopPropagation();
				this.close();
				return false;
			} else {
				return true;
			}
		}));
	}

	open(entityName: string, id?: string, tab?: string, parentid?: string) {
		entityName = entityName.toLowerCase();
		let path: string = this.entityPathMap[entityName];

		if (parentid) {
			path = path.replace(':parentid', parentid);
		}

		if (path) {
			if (tab && !id) {
				this.router.navigate(['', { outlets: { modal: [...path.split('/'), tab] } }]);
			} else if (!tab && this.defaultEntityTab[entityName]) {
				tab = this.defaultEntityTab[entityName];
				this.router.navigate(['', { outlets: { modal: [...path.split('/'), id, tab] } }]);
			} else if (tab) {
				this.router.navigate(['', { outlets: { modal: [...path.split('/'), id, tab] } }]);
			} else {
				this.router.navigate(['', { outlets: { modal: [...path.split('/'), id] } }]);
			}
		} else {
			console.warn(`Warning: ModalRouteService entity type ${entityName} not implemented yet.`);
		}
	}

	close() {
		this.router.navigate(['', { outlets: { modal: null } }]);
	}

	navToInitialNavigationUrl(url) {
		const newRoute = url;
		const matches = this.modalRegex.exec(url);
		if (matches && matches[1]) {
			const primaryUrl = newRoute.split('(modal')[0];
			this.router.navigate([primaryUrl]);
			setTimeout(() => {
				this.router.navigate( this.convertToModalRoute([matches[1]]) );
			});
		} else {
			this.router.navigate([newRoute]);
		}
	}

	convertToModalRoute(routerLink: any, primaryRoute = '') {
		if (routerLink && routerLink.length) {
			const modalRouterLinkParts = [];
			routerLink.forEach(path => {
				path?.split('/').forEach(part => {
					if (part) {
						modalRouterLinkParts.push(part);
					}
				});
			});
			const defaultTab = this.defaultEntityTab[modalRouterLinkParts[0]] || this.defaultEntityTab[modalRouterLinkParts[1]];
			// asumes that length should be 4 for now, better solution would be to have a redirect in app.module
			if (modalRouterLinkParts.length < 4 && modalRouterLinkParts[ modalRouterLinkParts.length - 1] !== defaultTab && defaultTab) {
				modalRouterLinkParts.push(defaultTab);
			}
			return [primaryRoute, { outlets: { modal: modalRouterLinkParts } }];
		}
	}
}
